import { Culture } from "@kolibrisoftware/customerportal-api";
import { getLocaleFromCulture } from "./culture";

/**
 * Method for formatting prices into a standard that design team has decided.
 * eg. 1.000,00 becomes 1.000,- in dutch and 1000 in English. See inline comments.
 * Takes the localization into account.
 * @param price The number to format
 * @param culture Locale to use to obtain the correct translation
 * @returns The price formatted according to Kolibri and localization standards
 */
export function getFormattedPrice(
  price: number | undefined | null,
  culture: Culture
): string {
  if (price == null) return "";

  const locale = getLocaleFromCulture(culture);
  const isDutchLocale = locale === "nl-NL";
  const isWholeNumber = price % 1 === 0;
  const showDecimals = price < 1000 && !isWholeNumber;

  // Decide how many decimal digits to show
  let minimumFractionDigits: number;
  let maximumFractionDigits: number;

  if (isDutchLocale) {
    // In Dutch: show no decimals for whole numbers, otherwise 2
    minimumFractionDigits = isWholeNumber ? 0 : 2;
    maximumFractionDigits = 2;
  } else {
    // Other locales: show 2 decimals if price < 1000, otherwise none
    minimumFractionDigits = showDecimals ? 2 : 0;
    maximumFractionDigits = showDecimals ? 2 : 0;
  }

  const formatted = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(price);

  // Add ',-' for Dutch whole numbers
  if (isDutchLocale && isWholeNumber) {
    return `${formatted},-`;
  }

  return formatted;
}
